import { FC } from 'react'
import { CustomSelect, ErrorLabel, Input, Label, Radio, TextEditor } from '../../../../components'
import { InputType } from '../../../../../_metronic/helpers'
import { CampoResolvido } from '../formularios-create/core/_models'

export type ValorCampo = string | string[]

type Props = {
  campo: CampoResolvido
  valor: ValorCampo
  erro?: string
  disabled?: boolean
  /** Texto de apoio (ex.: resposta anterior que não casou com nenhuma opção atual). */
  ajuda?: string
  onChange: (valor: ValorCampo) => void
  onBlur?: () => void
  registrarFoco?: (elemento: HTMLElement | null) => void
}

const comoTexto = (valor: ValorCampo): string => (Array.isArray(valor) ? valor[0] ?? '' : valor)

const comoLista = (valor: ValorCampo): string[] =>
  Array.isArray(valor) ? valor : valor === '' ? [] : [valor]

/**
 * Um campo do formulário em preenchimento.
 *
 * Em RADIO/SELECT o **valor** é sempre o `codigo` da opção (o rótulo é só exibição), como o envio
 * da T8a espera. Cada controle tem nome acessível: `<label htmlFor>` nos campos simples e
 * `fieldset`/`legend` no grupo de rádio.
 */
export const CampoPreenchimento: FC<Props> = ({
  campo,
  valor,
  erro,
  disabled,
  ajuda,
  onChange,
  onBlur,
  registrarFoco,
}) => {
  const id = `campo-${campo.codigo}`
  const descricaoId = campo.instrucoes ? `${id}-instrucoes` : undefined

  const instrucoes = campo.instrucoes ? (
    <span id={descricaoId} className='text-muted small pt-1 d-block'>
      {campo.instrucoes}
    </span>
  ) : null

  const complemento = ajuda ? <span className='text-muted small pt-1 d-block'>{ajuda}</span> : null

  if (campo.tipo === InputType.Radio) {
    return (
      <fieldset className='mb-3 border-0 p-0' aria-describedby={descricaoId}>
        <legend className={`fs-7 fw-bolder ${campo.obrigatorio ? 'required' : ''}`}>
          {campo.rotulo}
        </legend>
        <div ref={registrarFoco}>
          <Radio<string>
            id={id}
            options={campo.opcoes.map((opcao) => ({
              id: opcao.codigo,
              descricao: opcao.descricao,
              value: opcao.codigo,
            }))}
            value={comoTexto(valor)}
            disabled={disabled}
            touched={!!erro}
            error={erro}
            onChange={onChange}
            onBlur={onBlur}
          />
        </div>
        {instrucoes}
        {complemento}
        {erro && <ErrorLabel>{erro}</ErrorLabel>}
      </fieldset>
    )
  }

  const rotulo = (
    <Label className='mb-2' htmlFor={id} isRequired={campo.obrigatorio}>
      {campo.rotulo}
    </Label>
  )

  if (campo.tipo === InputType.Select) {
    const opcoes = campo.opcoes.map((opcao) => ({ value: opcao.codigo, label: opcao.descricao }))
    const selecionados = comoLista(valor)
      .map((codigo) => opcoes.find((opcao) => opcao.value === codigo))
      .filter((opcao): opcao is { value: string; label: string } => !!opcao)

    return (
      <div className='mb-3'>
        {rotulo}
        <div ref={registrarFoco}>
          <CustomSelect
            inputId={id}
            name={campo.codigo}
            isMulti={campo.multiplo}
            options={opcoes}
            isRequired={campo.obrigatorio}
            isDisabled={disabled}
            isInvalid={!!erro}
            value={campo.multiplo ? selecionados : selecionados[0]}
            onChange={(selecionado) => {
              if (Array.isArray(selecionado)) {
                onChange(selecionado.map((opcao) => String(opcao.value)))
                return
              }
              onChange(selecionado ? String(selecionado.value) : campo.multiplo ? [] : '')
            }}
            onBlur={onBlur}
          />
        </div>
        {instrucoes}
        {complemento}
        {erro && <ErrorLabel>{erro}</ErrorLabel>}
      </div>
    )
  }

  if (campo.tipo === InputType.TextEditor) {
    return (
      <div className='mb-3'>
        {rotulo}
        <div ref={registrarFoco} id={id}>
          <TextEditor
            value={comoTexto(valor)}
            onChange={(texto: string) => onChange(texto)}
            disabled={disabled}
          />
        </div>
        {instrucoes}
        {complemento}
        {erro && <ErrorLabel>{erro}</ErrorLabel>}
      </div>
    )
  }

  return (
    <div className='mb-3'>
      {rotulo}
      <div ref={registrarFoco}>
        <Input
          id={id}
          name={campo.codigo}
          type={campo.tipo === InputType.Number ? 'number' : 'text'}
          required={campo.obrigatorio}
          disabled={disabled}
          isInvalid={!!erro}
          value={comoTexto(valor)}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      </div>
      {instrucoes}
      {complemento}
      {erro && <ErrorLabel>{erro}</ErrorLabel>}
    </div>
  )
}
